import { Modal } from '@components/core/modal.component';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useCreateMeiaThreadSwimlane } from '@queries/meia.query';
import { type MeiaSwimlaneFormValues, meiaSwimlaneSchema } from '@schemas/meia.schema';
import { createFormInvalidHandler } from '@utils/form.util';
import type { FC } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';

type MeiaAddCustomSwimlaneModalProps = {
  isOpen: boolean;
  onClose: () => void;
  position: number;
  studyId: string;
  threadId: string;
};

export const MeiaAddCustomSwimlaneModal: FC<MeiaAddCustomSwimlaneModalProps> = (props) => {
  const { isOpen, onClose, position, studyId, threadId } = props;
  const { isPending, mutateAsync: createSwimlane } = useCreateMeiaThreadSwimlane();

  const defaultValues: MeiaSwimlaneFormValues = {
    entryCriteria: '',
    exitCriteria: '',
    operationalPurpose: '',
    phaseName: '',
  };

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const validation = meiaSwimlaneSchema.safeParse(value);
      if (!validation.success) {
        errorToast({ description: 'Please complete every required field.' });
        return;
      }
      const payload = validation.data;
      await createSwimlane({
        entryCriteria: payload.entryCriteria,
        exitCriteria: payload.exitCriteria,
        operationalPurpose: payload.operationalPurpose,
        phaseName: payload.phaseName,
        position,
        studyId,
        threadId,
      });
      successToast({ description: 'Stage added.' });
      onClose();
    },
    onSubmitInvalid: createFormInvalidHandler<typeof defaultValues>(),
    validators: { onSubmit: meiaSwimlaneSchema },
  });

  return (
    <Modal
      centered
      footer={{
        rightButton: {
          children: isPending ? 'Adding...' : 'Add Stage',
          disabled: isPending,
          onClick: () => void form.handleSubmit(),
        },
      }}
      header={{ title: 'Add Custom Stage' }}
      isOpen={isOpen}
      onClose={onClose}
      width="560px"
    >
      <form
        {...MEIA_AUTOFILL_IGNORE_PROPS}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.AppField name="phaseName">
            {(field) => (
              <field.TextField
                {...MEIA_AUTOFILL_IGNORE_PROPS}
                label="Stage Name"
                placeholder="e.g., Detect"
                required
              />
            )}
          </form.AppField>
          <form.AppField name="operationalPurpose">
            {(field) => (
              <field.TextAreaField
                label="Operational Purpose"
                placeholder="What outcome does this Stage produce for the Thread?"
                rows={2}
              />
            )}
          </form.AppField>
          <form.AppField name="entryCriteria">
            {(field) => (
              <field.TextAreaField
                label="Entry Criteria"
                placeholder="Conditions before entering this Stage."
                rows={2}
              />
            )}
          </form.AppField>
          <form.AppField name="exitCriteria">
            {(field) => (
              <field.TextAreaField
                label="Exit Criteria"
                placeholder="Conditions before leaving this Stage."
                rows={2}
              />
            )}
          </form.AppField>
        </form.AppForm>
      </form>
    </Modal>
  );
};
